import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, PlanPicker, describeAvailablePasses } from '@copilot/ui';
import Icon from '../components/common/Icon';
import Loader from '../components/common/Loader';
import { useAuth } from '../hooks/useAuth';
import { PAYMENT_NOTICE } from '../constants/legalContent';
import { MeetingPassCheckoutNotice, useMeetingPassCheckout } from '../hooks/useMeetingPassCheckout';
import { CheckoutStageNotice, useRazorpayCheckout } from '../hooks/useRazorpayCheckout';
import { ANALYTICS_EVENTS, track } from '../services/analytics';
import * as billingApi from '../services/billingApi';
import * as meetingPassApi from '../services/meetingPassApi';
import * as pricingApi from '../services/pricingApi';
import { clearPostAuthPath } from '../utils/authRedirects';

// In-app plan picker (/app/billing/plans), inside AppShell. The picker
// itself is shared with Desktop (@copilot/ui's PlanPicker); this page
// supplies Web's API calls and its paid-checkout step - Razorpay's
// window, run in the page.
function PlansPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const goToBilling = () => navigate('/app/billing');

  // Once a paid plan has actually activated (webhook granted, not just
  // "checkout closed"), go to Plan & Billing, where the new plan, its
  // renewal date and the charge show up.
  const checkout = useRazorpayCheckout({ user, onActivated: goToBilling });

  // Pay Per Meeting: a one-time purchase of one meeting, beside the
  // plans. Its own checkout (a one-time order) and its own state - it is
  // never routed through the plan picker's api below, so buying one can
  // never start a subscription checkout or a plan-change request.
  const [searchParams] = useSearchParams();
  const arrivedToBuy = searchParams.get('buy') === 'meeting-pass';
  const [passProduct, setPassProduct] = useState(null);
  const [passStatus, setPassStatus] = useState(null);
  const [passError, setPassError] = useState('');
  const passCheckout = useMeetingPassCheckout({ user, onPurchased: setPassStatus });

  useEffect(() => {
    // This page is where a visitor sent to sign in for "Buy 1 Meeting"
    // was heading; they have arrived, so the reminder is spent.
    clearPostAuthPath();
    track(ANALYTICS_EVENTS.PRICING_VIEWED, { source: 'in_app', intent: arrivedToBuy ? 'meeting_pass' : null });

    let cancelled = false;

    // Best-effort: the plans must render whether or not this loads.
    meetingPassApi
      .getProduct()
      .then((product) => {
        if (!cancelled) setPassProduct(product);
      })
      .catch(() => {});
    meetingPassApi
      .getMyPasses()
      .then((status) => {
        if (!cancelled) setPassStatus(status);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // Runs once, on arrival - arrivedToBuy is read from that first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buyMeetingPass = async () => {
    setPassError('');
    try {
      await passCheckout.start();
    } catch (err) {
      setPassError(err?.message || 'Unable to start the purchase. Please try again.');
    }
  };

  const api = useMemo(
    () => ({
      getPlans: () => pricingApi.getPublicPricing(),
      // The server's card copy, shared feature list and FAQ.
      getCatalogue: () => billingApi.getPricingCatalogue(),
      async getAccount() {
        const [summary, pendingRequest, billing] = await Promise.all([
          pricingApi.getMySubscription(),
          pricingApi.getMyPendingRequest(),
          billingApi.getMyBilling(),
        ]);
        return {
          subscription: summary.subscription ?? null,
          pendingRequest,
          billingEnabled: Boolean(billing?.billing_enabled),
        };
      },
      requestPlanChange: (planId, cycle) => pricingApi.requestPlanChange(planId, cycle),
      startCheckout: (plan, cycle) => checkout.start(plan, cycle),
      describeError: (error, fallback) => ({ message: error?.message || fallback, code: error?.code }),
    }),
    // checkout.start is stable; the picker must not reload on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkout.start]
  );

  return (
    <PlanPicker
      api={api}
      loadingIndicator={<Loader label="Loading plans..." />}
      headerAction={
        <Link to="/app/billing" className="plans-button plans-button-secondary">
          <Icon name="close" size={14} />
          Back to billing
        </Link>
      }
      notice={
        <>
          {/* Shown before any checkout button: the no-refund rule the customer
              accepts by paying. Its full text is in the Terms of Service. */}
          <div className="form-alert billing-alert-notice" role="note" data-testid="payment-notice">
            {PAYMENT_NOTICE.text}{' '}
            <Link to={PAYMENT_NOTICE.path} target="_blank" rel="noreferrer">
              {PAYMENT_NOTICE.linkLabel}
            </Link>
          </div>
          <CheckoutStageNotice
            stage={checkout.stage}
            onRecheck={checkout.recheck}
            onGoToBilling={goToBilling}
          />
          {/* Arrived from "Buy 1 Meeting" on the pricing page. Points at
              the card; checkout opens only when its button is pressed. */}
          {arrivedToBuy && passProduct && !passCheckout.stage && (
            <div className="form-alert billing-alert-notice" role="status">
              {pricingApi.PAY_PER_MEETING.name}: {pricingApi.formatPassPrice(passProduct)}{' '}
              {pricingApi.PAY_PER_MEETING.priceSuffix} for{' '}
              {pricingApi.formatPassMeetings(passProduct)}. Use {pricingApi.PAY_PER_MEETING.cta} below to
              continue.
            </div>
          )}
          <MeetingPassCheckoutNotice stage={passCheckout.stage} onRecheck={passCheckout.recheck} />
          {passError && (
            <div className="form-alert" role="alert">
              {passError}
            </div>
          )}
          {passStatus?.available_passes > 0 && (
            <div className="plans-pending-banner" role="status">
              {describeAvailablePasses(passStatus.available_passes)}.
            </div>
          )}
        </>
      }
      // Not a plan: bought with its own one-time checkout above.
      payPerMeeting={{
        product: passProduct,
        action: (
          <Button
            block
            variant="secondary"
            disabled={!passProduct?.available || passCheckout.isBusy}
            onClick={buyMeetingPass}
          >
            {passCheckout.isBusy ? 'Processing…' : pricingApi.PAY_PER_MEETING.cta}
          </Button>
        ),
      }}
      // The Custom card is not a plan: its only action is this link to
      // the public contact form, which prefills from the session. No
      // plan-change request, no checkout.
      customPlanAction={
        <Link to={pricingApi.CUSTOM_PLAN.contactPath} className="btn btn-secondary btn-block">
          {pricingApi.CUSTOM_PLAN.cta}
        </Link>
      }
    />
  );
}

export default PlansPage;
